import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Plus, Edit, Trash2, Save, X, PlusCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import './Adicionais.css';

const Adicionais = () => {
  const [modalAberto, setModalAberto] = useState(false);
  const [adicionalEditando, setAdicionalEditando] = useState(null);
  const queryClient = useQueryClient();

  const { data: estabelecimentoData } = useQuery(
    'estabelecimento-adicionais',
    async () => {
      const response = await api.get('/api/estabelecimentos/me/estabelecimento');
      return response.data.data.estabelecimento;
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const estabelecimentoId = estabelecimentoData?._id;

  const { data: adicionais, isLoading, error, refetch } = useQuery(
    ['adicionais', estabelecimentoId],
    async () => {
      if (!estabelecimentoId) return [];
      const response = await api.get(`/api/adicionais?estabelecimento=${estabelecimentoId}`);
      return response.data.data.adicionais;
    },
    {
      enabled: !!estabelecimentoId,
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Erro ao carregar adicionais');
      }
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/api/adicionais/${id}`),
    {
      onSuccess: () => {
        toast.success('Adicional removido!');
        queryClient.invalidateQueries(['adicionais', estabelecimentoId]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Erro ao remover adicional');
      }
    }
  );

  const handleDelete = (id, nome) => {
    if (window.confirm(`Tem certeza que deseja deletar o adicional "${nome}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  // Agrupar adicionais por categoria
  const adicionaisPorCategoria = React.useMemo(() => {
    if (!adicionais) return {};
    return adicionais.reduce((acc, adicional) => {
      const categoria = adicional.categoria || 'Geral';
      if (!acc[categoria]) {
        acc[categoria] = [];
      }
      acc[categoria].push(adicional);
      return acc;
    }, {});
  }, [adicionais]);

  if (isLoading) {
    return <div className="loading">Carregando adicionais...</div>;
  }

  if (error) {
    return <div className="error-message">Erro ao carregar adicionais: {error.message}</div>;
  }

  return (
    <div className="adicionais-page">
      <div className="page-header">
        <div>
          <h2><PlusCircle size={28} /> Adicionais Globais</h2>
          <p>Gerencie os adicionais que aparecem em todos os produtos</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setAdicionalEditando(null); setModalAberto(true); }}>
          <Plus size={20} />
          Novo Adicional
        </button>
      </div>

      {Object.keys(adicionaisPorCategoria).length === 0 ? (
        <p className="text-muted">Nenhum adicional cadastrado ainda. Clique em "Novo Adicional" para começar.</p>
      ) : (
        Object.entries(adicionaisPorCategoria).map(([categoria, adicionaisCategoria]) => (
          <div key={categoria} className="categoria-section">
            <h3 className="categoria-title">{categoria}</h3>
            <div className="adicionais-grid">
              {adicionaisCategoria.map(adicional => (
                <div key={adicional._id} className="adicional-card">
                  <div className="adicional-info">
                    <h3 className="adicional-nome">{adicional.nome}</h3>
                    {adicional.descricao && (
                      <p className="adicional-descricao">{adicional.descricao}</p>
                    )}
                    <div className="adicional-preco">R$ {adicional.preco.toFixed(2)}</div>
                    <span className={`adicional-status ${adicional.disponivel ? 'disponivel' : 'indisponivel'}`}>
                      {adicional.disponivel ? 'Disponível' : 'Indisponível'}
                    </span>
                  </div>
                  <div className="adicional-actions">
                    <button 
                      className="btn btn-icon btn-edit" 
                      onClick={() => { setAdicionalEditando(adicional); setModalAberto(true); }}
                    >
                      <Edit size={18} />
                    </button>
                    <button 
                      className="btn btn-icon btn-delete" 
                      onClick={() => handleDelete(adicional._id, adicional.nome)}
                      disabled={deleteMutation.isLoading}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {modalAberto && (
        <ModalAdicional
          adicional={adicionalEditando}
          onClose={() => { setModalAberto(false); setAdicionalEditando(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries(['adicionais', estabelecimentoId]);
            setModalAberto(false);
          }}
          estabelecimentoId={estabelecimentoId}
        />
      )}
    </div>
  );
};

const ModalAdicional = ({ adicional, onClose, onSuccess, estabelecimentoId }) => {
  const [formData, setFormData] = useState({
    nome: adicional?.nome || '',
    descricao: adicional?.descricao || '',
    preco: adicional?.preco || 0,
    categoria: adicional?.categoria || 'Geral',
    disponivel: adicional?.disponivel !== false,
    ordem: adicional?.ordem || 0,
    icone: adicional?.icone || ''
  });
  const queryClient = useQueryClient();

  const saveMutation = useMutation(
    (data) => {
      if (adicional) {
        return api.put(`/api/adicionais/${adicional._id}`, data);
      }
      return api.post('/api/adicionais', { ...data, estabelecimento: estabelecimentoId });
    },
    {
      onSuccess: () => {
        toast.success(adicional ? 'Adicional atualizado!' : 'Adicional criado!');
        onSuccess();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Erro ao salvar adicional');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>{adicional ? 'Editar' : 'Novo'} Adicional</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nome do Adicional *</label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              required
              placeholder="Ex: Queijo extra, Bacon, Refrigerante"
            />
          </div>
          <div className="form-group">
            <label>Descrição</label>
            <textarea
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              rows={3}
              placeholder="Descrição opcional do adicional"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Preço (R$) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.preco}
                onChange={(e) => setFormData({ ...formData, preco: parseFloat(e.target.value) || 0 })}
                required
              />
            </div>
            <div className="form-group">
              <label>Categoria</label>
              <input
                type="text"
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                placeholder="Ex: Bebidas, Acompanhamentos, Molhos"
              />
              <small>Agrupa os adicionais na modal do produto</small>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Ordem</label>
              <input
                type="number"
                value={formData.ordem}
                onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                min="0"
              />
              <small>Define a ordem de exibição (menor primeiro)</small>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.disponivel}
                  onChange={(e) => setFormData({ ...formData, disponivel: e.target.checked })}
                />
                {' '}Disponível
              </label>
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saveMutation.isLoading}>
              <Save size={20} />
              {saveMutation.isLoading ? 'Salvando...' : 'Salvar Adicional'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Adicionais;










